<template>
  <div class="tool_tip">
    <button
      @click.prevent
      aria-describedby="tool_tip"
      @mouseenter="handleShow"
      @mouseleave="handleHide"
      @focus="handleShow"
      @blur="handleHide"
      ref="icon"
    >
      <component
        v-if="labelIcon"
        :is="labelIcon"
      />
    </button>

    <div
      ref="tooltip"
      class="tooltipText"
      role="tooltip"
      :style="{ maxWidth, backgroundColor: bgColor, color }"
    >
      {{ text }}
      <div class="tooltipArrow" data-popper-arrow></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from "vue";
import { createPopper, type Instance } from "@popperjs/core";

import { type TooltipProps } from "@/types";

const props = defineProps<TooltipProps>();

// Refs for DOM nodes
const icon = ref<HTMLElement | null>(null);
const tooltip = ref<HTMLElement | null>(null);

// Popper instance
let popperInstance: Instance | null = null;

const createInstance = () => {
  if (icon.value && tooltip.value) {
    popperInstance = createPopper(icon.value, tooltip.value, {
      placement: props.placement ?? "top",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 10],
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top", "bottom"],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 10,
          },
        },
      ],
      strategy: "absolute",
    });
  }
};

const handleShow = () => {
  if (!popperInstance) createInstance();
  tooltip.value?.setAttribute("data-show", "");
  popperInstance?.update();
};

const handleHide = () => {
  tooltip.value?.removeAttribute("data-show");
};

onUnmounted(() => {
  popperInstance?.destroy();
  popperInstance = null;
});
</script>


<style scoped>
.tooltipText {
  padding: 0.5rem 1rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: none;
  border-radius: 5px;
  display: none;
  transition: all ease-in-out 0.3s;
}

.tooltipText[data-show] {
  display: block;
}

.tooltipArrow,
.tooltipArrow::before {
  position: absolute;
  width: 0.5rem;
  height: 0.5rem;
  background: inherit;
}

.tooltipArrow {
  visibility: hidden;
}

.tooltipArrow::before {
  visibility: visible;
  content: "";
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

.tooltipText[data-popper-placement^="top"] > .tooltipArrow {
  bottom: -4px;
}

.tooltipText[data-popper-placement^="bottom"] > .tooltipArrow {
  top: -4px;
}

.tooltipText[data-popper-placement^="left"] > .tooltipArrow {
  right: -4px;
}

.tooltipText[data-popper-placement^="right"] > .tooltipArrow {
  left: -4px;
}
</style>
